import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box } from '@mui/material';
import { useUserStore } from '../../app/store/useUserStore';
import { type UserRole } from './types';
import { Skeleton } from '../../shared/components/common/SkeletonLoaders/Skeleton';
import { getAuth0Token } from '../../auth/utils';
import { isTokenValid } from '../../auth/tokenService';
import { getReAuthFlag } from '../../shared/utils/reAuthFlag';


interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: UserRole[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
}) => {
  const location = useLocation();
  const { isLoading: auth0Loading, isAuthenticated: auth0Authenticated } = useAuth0();
  const { user, isProfileLoading } = useUserStore();
  const [hasValidToken, setHasValidToken] = useState<boolean | null>(null);

  // Check token validity
  useEffect(() => {
    const checkToken = async () => {
      try {
        const token = getAuth0Token();
        if (token) {
          const isValid = isTokenValid();
          setHasValidToken(isValid);
        } else {
          setHasValidToken(false);
        }
      } catch {
        setHasValidToken(false);
      }
    };

    if (auth0Authenticated) {
      checkToken();
    } else {
      // During re-authentication, Auth0's isAuthenticated briefly flips to false.
      // Don't reset the token validity in that case to avoid a redirect flash.
      if (getReAuthFlag()) {
        return;
      }
      setHasValidToken(false);
    }
  }, [auth0Authenticated, user?.user_id]);

  // Show loading spinner while checking auth
  const isLoading = auth0Loading || isProfileLoading || hasValidToken === null;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: '24px',
          gap: '16px',
        }}
      >
        <Skeleton variant="rectangular" width="100%" height="60px" sx={{ maxWidth: '800px', borderRadius: '8px' }} />
        <Skeleton variant="rectangular" width="100%" height="400px" sx={{ maxWidth: '800px', borderRadius: '8px' }} />
      </Box>
    );
  }

  // Multi-factor authentication check
  const isFullyAuthenticated =
    auth0Authenticated;

  // Redirect to search-results if not authenticated
  if (!isFullyAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Block access to any protected route until profile is complete
  // (skip this check on /update-profile itself to avoid infinite redirect)
  if (location.pathname !== '/update-profile') {
    // If the user has no role yet, send them to home so the role selection
    // dialog appears over the home page (not the update-profile page)
    if (!user?.user_role) {
      return <Navigate to="/" replace />;
    }

    // first_login is the primary signal; fall back to field-completeness
    // when it's false/undefined (e.g. user left profile incomplete previously)
    const isProfileComplete =
      !!(user?.full_name?.trim().split(' ').filter(Boolean).length >= 2) &&
      !!(user?.email) &&
      !!(user?.address?.street_no_one);

    const needsProfileUpdate = user?.first_login === true ? true : !isProfileComplete;

    if (needsProfileUpdate) {
      return <Navigate to="/update-profile" replace />;
    }
  }

  // Check role-based access
  if (allowedRoles && allowedRoles.length > 0) {
    const userRole = user?.user_role;

    // Allow empty role for /update-profile page
    const isAllowed = userRole && allowedRoles.includes(userRole as UserRole);

    if (!isAllowed) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};
